import Analytics from '@/utils/analytics';
import CatalogEmptyState from '@assets/icons/catalog-empty-state.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import ScanIcon from '@assets/icons/scan.svg';
import SearchIcon from '@assets/icons/search.svg';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CATALOG_ROUTES } from '@/navigators/routes';
import { CatalogStackParams } from '@/navigators/types';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { FONTS, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import { FulfillmentAvailability } from '@/components/partners/fulfillment-availability';
import { ItemRecommendationCarousel } from '@/components/orders/item-recommendation-carousel';
import { ItemSearchHeaderContainer } from '@/components/orders/item-search-header-container';
import { KeyboardAwareRecyclerList } from '@/components/recycler-list';
import {
  LayoutSize,
  useLayoutContext,
  useLayoutSizeValue,
} from '@/contexts/layout-context';
import { OfflineComponent } from '@/components/offline-component';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, Text, View } from 'react-native';
import {
  SupplierCatalogCategoryListItem,
  useCategoryListItemHeight,
} from '@/components/orders/supplier-catalog-category-list-item';
import { useCatalogCategoriesSections } from '@/hooks/use-catalog-category-sections';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useTheme } from '@/contexts/theme-context';
import { ViewOrderButton } from '@/components/partners/view-order-button';
import { VolumePromotionsCarousel } from '@/components/orders/volume-promotions-carousel';

const LAYOUT_SIZE_TO_CATEGORY_COLUMNS_MAP: { [size in LayoutSize]: number } = {
  [LayoutSize.SMALL]: 2,
  [LayoutSize.SMALL_TABLET]: 2,
  [LayoutSize.MEDIUM]: 3,
  [LayoutSize.LARGE]: 4,
};

interface Props {
  navigation: StackNavigationProp<CatalogStackParams>;
}

export const CatalogScreen: React.FC<Props> = ({ navigation }) => {
  const baseNavHeaderProps = useHeaderRestaurantSelectorProps();
  const theme = useTheme();
  const { itemRecommendations } = useItemRecommendationsContext();
  const { isSmallLayout } = useLayoutContext();
  const { showBarcodeScanner } = useNewOrderContext();
  const isOfflineModeActive = useOfflineModeActive();

  const categoryColumnCount = useLayoutSizeValue(
    LAYOUT_SIZE_TO_CATEGORY_COLUMNS_MAP,
  );

  const categoryItemHeight = useCategoryListItemHeight();
  const { categoriesSectionData, hasResponse } = useCatalogCategoriesSections();

  const itemSearchHeaderContainerProps = useMemo(() => {
    const onScanPress = () => {
      navigation.push(CATALOG_ROUTES.Scan);
    };

    const actions: Action[] = showBarcodeScanner
      ? [
          {
            Icon: ScanIcon,
            iconProps: {
              stroke: theme.sk.color.icon.primary,
            },
            onPress: onScanPress,
            testID: 'scan-barcode-icon',
          },
        ]
      : [];

    actions.push({
      Icon: SearchIcon,
      iconProps: {
        fill: theme.colors.primary,
      },
      onPress: () => {
        navigation.push(CATALOG_ROUTES.Search);
      },
      testID: 'search-icon',
    });

    return {
      navigationHeaderProps: {
        ...baseNavHeaderProps,
        actions: isOfflineModeActive ? [] : actions,
        contentFooter: <FulfillmentAvailability />,
        responsive: true,
        title: i18n.t('catalog'),
      },
      onScanPress: showBarcodeScanner ? onScanPress : undefined,
      statusBarColor: theme.colors.background,
    };
  }, [
    baseNavHeaderProps,
    isOfflineModeActive,
    navigation,
    showBarcodeScanner,
    theme,
  ]);

  const sections = useMemo(
    () => [
      {
        data: categoriesSectionData,
        key: null,
      },
    ],
    [categoriesSectionData],
  );

  return (
    <ItemSearchHeaderContainer
      {...itemSearchHeaderContainerProps}
      animateNavigationHeader
      searchSource="catalog"
    >
      {(scrollViewProps) => (
        <>
          {!hasResponse && isOfflineModeActive ? (
            <View
              style={[
                styles.staticContentContainer,
                scrollViewProps.contentContainerStyle,
              ]}
            >
              <OfflineComponent />
            </View>
          ) : !hasResponse ? (
            <View
              style={[
                styles.staticContentContainer,
                scrollViewProps.contentContainerStyle,
              ]}
            >
              <ActivityIndicator testID="loading-indicator" />
            </View>
          ) : categoriesSectionData.length === 0 ? (
            <EmptyStateMinimal
              containerStyle={scrollViewProps.contentContainerStyle}
              ImageComponent={CatalogEmptyState}
              text={i18n.t('partner_supplier_catalog_empty_state.message')}
            />
          ) : (
            <KeyboardAwareRecyclerList
              estimatedItemSize={categoryItemHeight}
              ItemSeparatorComponent={null}
              ListHeaderComponent={
                <>
                  <VolumePromotionsCarousel
                    locationPrefix="supplier_catalog"
                    onNavigateToDetails={(promotionUUID) => {
                      navigation.navigate(
                        CATALOG_ROUTES.VolumePromotionDetail,
                        { promotionUUID },
                      );
                    }}
                    onSeeAllPress={() => {
                      Analytics.track(
                        'supplier_catalog.promotions_see_all.press',
                      );
                      navigation.navigate(CATALOG_ROUTES.VolumePromotionsList);
                    }}
                  />
                  {itemRecommendations.length > 0 ? (
                    <ItemRecommendationCarousel
                      items={itemRecommendations.map(
                        (ir) => ir?.metadata!.item,
                      )}
                      onSeeAllPress={() => {
                        navigation.push(CATALOG_ROUTES.ItemRecommendationsList);
                      }}
                    />
                  ) : null}
                  {categoriesSectionData.length > 0 ? (
                    <View
                      onStartShouldSetResponder={() => true}
                      style={[
                        styles.sectionHeader,
                        isSmallLayout ? null : styles.sectionHeaderLarge,
                      ]}
                    >
                      <Text style={[TYPOGRAPHY.large, FONTS.primaryBold]}>
                        {i18n.t('browse_by_category')}
                      </Text>
                    </View>
                  ) : null}
                </>
              }
              overrideItemLayout={(layout) => {
                layout.span = 1 / categoryColumnCount;
              }}
              renderItem={({ index, item }) => {
                return (
                  <SupplierCatalogCategoryListItem
                    edge={
                      (index - 1) % categoryColumnCount === 0
                        ? 'left'
                        : index % categoryColumnCount === 0
                          ? 'right'
                          : null
                    }
                    onPress={(categoryName) => {
                      navigation.push(CATALOG_ROUTES.CategoryList, {
                        category: {
                          name: categoryName,
                        },
                      });
                    }}
                    option={item}
                  />
                );
              }}
              sections={sections}
              {...scrollViewProps}
              contentContainerStyle={[
                scrollViewProps.contentContainerStyle,
                styles.contentContainerStyle,
              ]}
            />
          )}
          <ViewOrderButton />
        </>
      )}
    </ItemSearchHeaderContainer>
  );
};

const styles = StyleSheet.create({
  contentContainerStyle: {
    paddingBottom: 8,
  },
  sectionHeader: {
    height: 44,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  sectionHeaderLarge: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  staticContentContainer: {
    flex: 1,
    marginTop: 24,
  },
});
